/**
 * Admin-related TypeScript types.
 * 
 * This module mirrors the backend schemas from app/schemas/admin.py
 * to provide type safety and consistency between frontend and backend.
 */

/**
 * Time range options for statistics queries.
 * Mirrors app.utils.time.TimeRange enum from backend.
 */
export enum TimeRange {
  DAY = "day",
  WEEK = "week", 
  MONTH = "month",
  THREE_MONTHS = "three_months",
  ALL = "all"
}

/**
 * User statistics response interface.
 * Mirrors UserStatsResponse from app.schemas.admin.py
 */
export interface UserStatsResponse {
  time_range: TimeRange;
  new_users: number;
  new_questions: number;
  new_sessions: number;
  generated_at: string; // ISO datetime string
}

/**
 * System-wide metrics response interface.
 * Mirrors SystemMetricsResponse from app.schemas.admin.py
 */
export interface SystemMetricsResponse {
  total_users: number;
  total_questions: number;
  total_sessions: number;
  active_users_today: number;
  generated_at: string; // ISO datetime string
}

/**
 * Base interface for administrative statistics.
 * Mirrors AdminStatsBase from app.schemas.admin.py
 */
export interface AdminStatsBase {
  generated_at: string; // ISO datetime string
  time_period?: string | null;
}

/**
 * Period-based statistics with flexible metrics.
 * Mirrors PeriodStatsResponse from app.schemas.admin.py
 */
export interface PeriodStatsResponse extends AdminStatsBase {
  metrics: Record<string, unknown>; // Key-value pairs of metric names and values
  period_start: string; // ISO datetime string
  period_end: string; // ISO datetime string
}

/**
 * Request schema for filtering statistics (future use).
 * Mirrors StatsFilterRequest from app.schemas.admin.py
 */
export interface StatsFilterRequest {
  time_range?: TimeRange;
  include_inactive?: boolean;
  user_group_ids?: number[] | null;
}

/**
 * Standard API response wrapper for admin endpoints.
 * Mirrors BaseResponse[T] from app.schemas.base.py
 */
export interface AdminApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
  metadata?: Record<string, unknown> | null;
}

// Type aliases for common response patterns
export type UserStatsApiResponse = AdminApiResponse<UserStatsResponse>;
export type SystemMetricsApiResponse = AdminApiResponse<SystemMetricsResponse>;
export type PeriodStatsApiResponse = AdminApiResponse<PeriodStatsResponse>; 